// src/features/system/devices/components/iot/IoTDeviceRegistryPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Full CRUD registry for devices — register new devices, edit labels,
// revoke, and blacklist.  Wired to /bff/device-ops/devices.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import AppDialog from '@/shared/components/ui/AppDialog';
import {
  registerDeviceApi,
  renameDeviceApi,
  updateDeviceTenantApi,
  revokeDeviceApi,
  blacklistDeviceApi,
  DEVICE_KEYS,
  type Device,
  type RegisterDeviceRequest,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';
import { useTenants } from '@/features/system/tenants/hooks/useTenants';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  } catch { return value; }
}

function timeAgo(value?: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch { return value; }
}

export default function IoTDeviceRegistryPanel() {
  const qc = useQueryClient();
  const { data: tenants = [] } = useTenants();

  const { data: devices = [], isLoading, error } = useDevicesList();

  // ── Tenant assignment ──────────────────────────────────────────────────
  const tenantMut = useMutation({
    mutationFn: ({ deviceId, tenantId }: { deviceId: string; tenantId: number }) =>
      updateDeviceTenantApi(deviceId, tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEVICE_KEYS.list }),
  });

  // ── Register dialog ────────────────────────────────────────────────────
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm] = useState<RegisterDeviceRequest>({ device_id: '' });

  const registerMut = useMutation({
    mutationFn: () => registerDeviceApi(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: DEVICE_KEYS.list }); setShowRegister(false); },
  });

  // ── Revoke dialog ──────────────────────────────────────────────────────
  const [revokeTarget, setRevokeTarget] = useState<Device | null>(null);

  const revokeMut = useMutation({
    mutationFn: () => revokeDeviceApi(revokeTarget!.device_id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: DEVICE_KEYS.list }); setRevokeTarget(null); },
  });

  // ── Blacklist dialog ───────────────────────────────────────────────────
  const [blacklistTarget, setBlacklistTarget] = useState<Device | null>(null);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklistLabel, setBlacklistLabel]   = useState('');

  const blacklistMut = useMutation({
    mutationFn: () => blacklistDeviceApi(blacklistTarget!.device_id, blacklistReason || undefined, blacklistLabel || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.list });
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.blacklist });
      setBlacklistTarget(null);
    },
  });

  // ── Rename dialog ──────────────────────────────────────────────────────
  const [renameTarget, setRenameTarget] = useState<Device | null>(null);
  const [renameLabel, setRenameLabel]   = useState('');

  const openRename = useCallback((d: Device) => {
    setRenameTarget(d);
    setRenameLabel(d.label ?? '');
  }, []);

  const renameMut = useMutation({
    mutationFn: () => renameDeviceApi(renameTarget!.device_id, renameLabel),
    onSuccess: () => { qc.invalidateQueries({ queryKey: DEVICE_KEYS.list }); setRenameTarget(null); },
  });

  // ── Table columns ──────────────────────────────────────────────────────
  const columns: ColumnDef<Device>[] = [
    { header: 'Device ID', accessor: 'device_id' },
    {
      header: 'Label',
      accessor: 'label',
      Cell: (v: string | null, row: Device) => (
        <button
          className="text-sky-400 hover:underline cursor-pointer text-left"
          onClick={() => openRename(row)}
        >
          {v || '(set label)'}
        </button>
      ),
    },
    { header: 'Type',     accessor: 'device_type', Cell: (v: string | null) => v ?? '—' },
    { header: 'Platform', accessor: 'platform',    Cell: (v: string | null) => v ?? '—' },
    {
      header: 'Active',
      accessor: 'is_active',
      Cell: (v: boolean) => v
        ? <span className="text-emerald-400 text-xs font-semibold">ACTIVE</span>
        : <span className="text-neutral-600 text-xs">INACTIVE</span>,
    },
    {
      header: 'Tenant',
      accessor: 'tenant_id',
      Cell: (v: number | null, row: Device) => (
        <select
          className="bg-neutral-900/60 border border-neutral-700 rounded px-1.5 py-0.5 text-xs text-neutral-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          value={v ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const tid = raw === '' ? 0 : Number(raw);
            if (!Number.isNaN(tid)) tenantMut.mutate({ deviceId: row.device_id, tenantId: tid });
          }}
        >
          <option value="">Unassigned</option>
          {tenants.map((t) => (
            <option key={t.id_tenant} value={t.id_tenant}>{t.tenant_name}</option>
          ))}
        </select>
      ),
      sortable: false,
    },
    { header: 'Created', accessor: 'created_at', Cell: (v: string | null) => <span className="text-xs text-neutral-500 cursor-default" title={formatDate(v)}>{timeAgo(v)}</span> },
    {
      header: '',
      accessor: '',
      Cell: (_: unknown, row: Device) => (
        <div className="flex gap-2">
          <button
            className="text-amber-500/60 hover:text-amber-400 text-xs"
            onClick={() => setRevokeTarget(row)}
          >
            Revoke
          </button>
          <button
            className="text-red-500/60 hover:text-red-400 text-xs"
            onClick={() => { setBlacklistTarget(row); setBlacklistReason(''); setBlacklistLabel(''); }}
          >
            Blacklist
          </button>
        </div>
      ),
      sortable: false,
    },
  ];

  return (
    <div className="h-full flex flex-col gap-3 overflow-hidden">
      {/* Header + New button */}
      <div className="flex items-center gap-3 shrink-0">
        <button
          className="px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-500 text-white text-xs font-medium"
          onClick={() => { setForm({ device_id: '' }); setShowRegister(true); }}
        >
          + Register Device
        </button>
        <span className="text-xs text-neutral-500">{devices.length} registered</span>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        {error ? (
          <div className="text-red-400 text-sm p-2">Error: {error.message}</div>
        ) : (
          <MinimalisticTable<Device>
            columns={columns}
            data={devices}
            noDataMessage={isLoading ? 'Loading…' : 'No devices registered.'}
            showNewButton={false}
          />
        )}
      </div>

      {/* ── Register dialog ───────────────────────────────────────────── */}
      <AppDialog
        open={showRegister}
        onOpenChange={setShowRegister}
        title="Register Device"
        description="Enter the device_id. For ESP32 devices this is the value shown by the `id` serial command."
        onConfirm={() => registerMut.mutate()}
        confirmLabel="Register"
        confirmDisabled={!form.device_id.trim()}
        isBusy={registerMut.isPending}
        size="sm"
      >
        <div className="space-y-3">
          <Field label="Device ID" value={form.device_id}
            onChange={v => setForm(f => ({ ...f, device_id: v }))} placeholder="e.g. plant-monitor-01" />
          <Field label="Label (optional)" value={form.label ?? ''}
            onChange={v => setForm(f => ({ ...f, label: v || undefined }))} placeholder="Living-room sensor" />
          <Field label="Device type (optional)" value={form.device_type ?? ''}
            onChange={v => setForm(f => ({ ...f, device_type: v || undefined }))} placeholder="esp32 / shelly / pacdaemon" />
        </div>
        {registerMut.isError && (
          <p className="text-red-400 text-xs mt-2">{(registerMut.error as Error).message}</p>
        )}
      </AppDialog>

      {/* ── Revoke dialog ─────────────────────────────────────────────── */}
      <AppDialog
        open={!!revokeTarget}
        onOpenChange={() => setRevokeTarget(null)}
        title="Revoke Device"
        description={`Revoke "${revokeTarget?.device_id}"? The device will need to re-authenticate.`}
        onConfirm={() => revokeMut.mutate()}
        confirmLabel="Revoke"
        confirmTone="danger"
        isBusy={revokeMut.isPending}
        size="sm"
      />

      {/* ── Blacklist dialog ──────────────────────────────────────────── */}
      <AppDialog
        open={!!blacklistTarget}
        onOpenChange={() => setBlacklistTarget(null)}
        title="Blacklist Device"
        description={`Permanently block "${blacklistTarget?.device_id}"? This prevents re-authorization until manually unblocked.`}
        onConfirm={() => blacklistMut.mutate()}
        confirmLabel="Blacklist"
        confirmTone="danger"
        isBusy={blacklistMut.isPending}
        size="sm"
      >
        <Field label="Label / Tag" value={blacklistLabel}
          onChange={setBlacklistLabel} placeholder="e.g. Rogue sensor, MAC spoofing" />
        <Field label="Reason (optional)" value={blacklistReason}
          onChange={setBlacklistReason} placeholder="e.g. Compromised device" />
      </AppDialog>

      {/* ── Rename dialog ─────────────────────────────────────────────── */}
      <AppDialog
        open={!!renameTarget}
        onOpenChange={() => setRenameTarget(null)}
        title="Edit Label"
        description={`Set a human-readable label for ${renameTarget?.device_id}.`}
        onConfirm={() => renameMut.mutate()}
        confirmLabel="Save"
        isBusy={renameMut.isPending}
        size="sm"
      >
        <Field label="Label" value={renameLabel} onChange={setRenameLabel} placeholder="Descriptive name" />
      </AppDialog>
    </div>
  );
}

// ── Reusable field ───────────────────────────────────────────────────────

function Field({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-neutral-400">{label}</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 rounded pac-glass-input
                   text-neutral-100 text-sm placeholder:text-neutral-600
                   focus:outline-none focus:ring-1 focus:ring-sky-500"
      />
    </label>
  );
}
